import { toast } from "react-toastify";

import {
  ApolloClient,
  createHttpLink,
  InMemoryCache,
  ApolloLink,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";

import config from "../config";
import {
  AffiliateBankInputTypeDef,
  AffiliateCompanyInputTypeDef,
  AffiliateGeneralInfoBaseInputTypeDef,
  StateTypesInputTypeDef,
  TrafficSourceTypesInputTypeDef,
  TrafficTypeInputTypeDef,
  YearsOfExperienceInputTypeDef,
  AffiliateUserInfoBaseInputTypeDef,
} from "./schema/affiliate/types";
import {
  CrmMappingRequestInputTypeDef,
  BrandBridgeParameterRequestInputTypeDef,
  ApiIntegrationCreateRequestInputTypeDef,
  BrandStatusInputTypeDef,
  UserStatusInputTypeDef,
  S2SIntegrationCreateRequestInputTypeDef,
  LinkS2SRequestInputTypeDef,
  LinkS2SRequestUpdateInputTypeDef,
  S2SIntegrationUpdateRequestInputTypeDef,
} from "./schema/brands/types";
import {
  ActivenessStatusInputTypeDef,
  ClickLimitInputTypeDef,
  DateRangeInputTypeDef,
  DayOfWeekEnumTypeDef,
  DeviceTypesEnumTypeDef,
  FiltersInputInputTypeDef,
  FlowActionTypeEnumTypeDef,
  FlowErrorTypeEnumTypeDef,
  FlowOfferSettingUpsertRequestInputTypeDef,
  FlowTypeEnumTypeDef,
  FlowUpsertRequestInputTypeDef,
  InversibleFilterOfDeviceTypesInputTypeDef,
  InversibleFilterOfStringInputTypeDef,
  OfferGroupTypeEnumTypeDef,
  ScheduleInputTypeDef,
  TimeCapacityTypesInputTypeDef,
} from "./schema/Campaigns/types";
import { RegistrationStatusPostbackTypeDef } from "./schema/pixel-tracking/types";
import {
  EntityPrivacyTypeDef,
  PixelTrackingEventTypeDef,
} from "./schema/registrations/types";
import { authHeader } from "../helpers/jwt-token-access/auth-token-header";

const affApiHttpLink = createHttpLink({
  uri: config.traffme.affiliateUrlApi + "graphql",
});

const authApiHttpLink = createHttpLink({
  uri: config.traffme.authUrlApi + "graphql",
});

const authLink = setContext((_, { headers }) => {
  const auth = authHeader();

  return {
    headers: {
      ...headers,
      authorization: auth?.Authorization || "",
    },
  };
});

const dynamicLink = new ApolloLink((operation, forward) => {
  const token = authHeader()?.Authorization;
  const selectedLink = token ? affApiHttpLink : authApiHttpLink;
  operation.setContext({
    headers: {
      ...operation.getContext().headers,
      authorization: token || "",
    },
  });
  return selectedLink.request(operation, forward);
});

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message }) => {
      toast.error(`${message}`);
    });
  }

  if (networkError) {
    toast.error("Network error");
  }
});

const client = new ApolloClient({
  link: ApolloLink.from([errorLink, authLink.concat(dynamicLink)]),
  cache: new InMemoryCache({
    addTypename: false,
  }),
  typeDefs: [
    CrmMappingRequestInputTypeDef,
    BrandBridgeParameterRequestInputTypeDef,
    ApiIntegrationCreateRequestInputTypeDef,
    BrandStatusInputTypeDef,
    UserStatusInputTypeDef,
    S2SIntegrationCreateRequestInputTypeDef,
    LinkS2SRequestInputTypeDef,
    LinkS2SRequestUpdateInputTypeDef,
    S2SIntegrationUpdateRequestInputTypeDef,
    AffiliateBankInputTypeDef,
    AffiliateCompanyInputTypeDef,
    AffiliateGeneralInfoBaseInputTypeDef,
    StateTypesInputTypeDef,
    TrafficSourceTypesInputTypeDef,
    TrafficTypeInputTypeDef,
    YearsOfExperienceInputTypeDef,
    AffiliateUserInfoBaseInputTypeDef,
    ActivenessStatusInputTypeDef,
    FlowUpsertRequestInputTypeDef,
    FlowActionTypeEnumTypeDef,
    FlowErrorTypeEnumTypeDef,
    FlowOfferSettingUpsertRequestInputTypeDef,
    FlowTypeEnumTypeDef,
    OfferGroupTypeEnumTypeDef,
    FiltersInputInputTypeDef,
    InversibleFilterOfStringInputTypeDef,
    ClickLimitInputTypeDef,
    DateRangeInputTypeDef,
    InversibleFilterOfDeviceTypesInputTypeDef,
    DeviceTypesEnumTypeDef,
    ScheduleInputTypeDef,
    DayOfWeekEnumTypeDef,
    PixelTrackingEventTypeDef,
    EntityPrivacyTypeDef,
    RegistrationStatusPostbackTypeDef,
    TimeCapacityTypesInputTypeDef,
  ],
});

export default client;
